import { ReactNode } from 'react';
import { Dialog, IconButton } from '@mui/material';
import {
  Close,
} from '@mui/icons-material';
import { ShareableCarousel } from 'components/Carousel/SharableCarousel';
import { CardUrls } from 'lib/types/replay';
import { useSnackbar } from 'notistack';
import { useApi } from 'lib/contexts/ApplicationState';
import { trackShareContentEvent } from 'lib/hooks/useEventTracking';

interface Props {
  onClose: () => void;
  text: ReactNode;
  cardUrls: CardUrls;
  /** The key of the slide that is currently being viewed */
  currentCard?: string;
}

export const ShareModal = ({
  onClose, text, cardUrls, currentCard,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const API = useApi();

  const imageUrlToBlob = async (imageUrl: string) => {
    const url = new URL(imageUrl);
    let response: Response;
    if (url.host === 'img.gondola.cc') {
      const pngImageUrl = `https://storage.googleapis.com/gondola-images/production${url.pathname}`;
      response = await fetch(pngImageUrl);
      return response.blob();
    }
    response = await fetch(imageUrl);
    return response.blob();
  };

  const shareImage = async (imageUrl: string, slideKey: string) => {
    const eventData = { content: 'replay2025', slide: slideKey };
    const filename = `gondola-replay-${slideKey}.png`;
    const blob = await imageUrlToBlob(imageUrl);
    try {
      if (navigator.share) {
        trackShareContentEvent(API, { ...eventData, shareType: 'share' });
        const file = new File([blob], filename, { type: blob.type });

        try {
          await navigator.share({
            files: [file],
          });
        } catch (err: any) {
          if (!err.toString().match('cancellation') && !err.toString().match('canceled')) {
            // If the error wasn't simply the user canceling the share, report it to the user
            enqueueSnackbar(`Unable to share the image: ${err}`, { variant: 'error' });
          }
        }
      } else if (navigator.clipboard) {
        trackShareContentEvent(API, { ...eventData, shareType: 'copy' });
        // eslint-disable-next-line no-undef
        const clipboardItem = new ClipboardItem({
          [blob.type]: blob,
        });
        await navigator.clipboard.write([clipboardItem]);
        enqueueSnackbar('Image copied!', { variant: 'success', autoHideDuration: 1500 });
      }
    } catch (err) {
      enqueueSnackbar('Share not supported, try saving the image or taking a screenshot.', { variant: 'warning' });
    }
  };

  const download = async (imageUrl: string, key: string) => {
    const filename = `gondola-replay-${key}.png`;
    trackShareContentEvent(API, { content: 'replay2025', slide: key, shareType: 'download' });
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const blobURL = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobURL;
      a.className = 'hidden';
      a.setAttribute('href', blobURL);
      a.setAttribute('download', filename);
      a.click();
    } catch (err) {
      enqueueSnackbar('Your current browser is not supported.', { variant: 'error' });
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
    >
      <div className="p-4">
        <div className="absolute right-2 top-2">
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </div>
        <div>
          {text}
        </div>
        <div className="flex mb-4">
          <ShareableCarousel
            imgUrls={cardUrls}
            onShare={shareImage}
            onDownload={download}
            currentCard={currentCard}
          />
        </div>
      </div>
    </Dialog>
  );
};
